import {
  ClockIcon,
  MailOpenIcon,
  SparklesIcon,
  TagIcon,
  UserIcon,
} from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import { HttpError, useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLodModal } from "components/historyLogModal";
import { SwitchButton } from "components/switchButton";
import { IFipixAccountDetail, IOrgEventIdsItem } from "interfaces";
import { useState } from "react";
import { Link } from "react-router-dom";
import { isHaveActionPermission } from "utills/helpers";

const EVENT_ALL_OPTION = { label: "All", value: "all" };

export const FipixAccountDetail: React.FC = () => {
  const parse = useParse();
  const { params } = parse();
  const [openLogModal, setOpenLogModal] = useState(false);

  const { data, isFetching, isPreviousData, isLoading } =
    useOne<IFipixAccountDetail>({
      resource: "fipix/accounts",
      id: params?.id,
      meta: {
        resource: "data",
      },
      queryOptions: {
        enabled: !!params?.id,
      } as any,
    });

  const fipixAccountDetailData = data?.data ?? null;

  const { data: organizationEventsData } = useOne<
    IOrgEventIdsItem[],
    HttpError
  >({
    resource: "fipix",
    id: `events`,
    meta: {
      resource: "data",
    },

    queryOptions: {
      enabled: !!fipixAccountDetailData,
    } as any,
  });

  const organizationEventIds = (
    (organizationEventsData?.data ?? []).length > 0 ? [EVENT_ALL_OPTION] : []
  ).concat(
    (organizationEventsData?.data ?? []).map((event: IOrgEventIdsItem) => ({
      value: event.id,
      label: event.name_en,
    }))
  );

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Fipix Account Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <Button
              className="bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200"
              onClick={() => setOpenLogModal(true)}
            >
              <span className="sr-only">History Log</span>
              <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">Log</span>
            </Button>
            {isHaveActionPermission("accounts", "fipix", "edit") ? (
              <Link to={`/fipix/accounts/${fipixAccountDetailData?.id}/edit`}>
                <Button>
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container p-0">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={TagIcon}
            label="Status"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                <SwitchButton
                  disabled
                  checked={fipixAccountDetailData?.is_active ?? false}
                  onChange={() => {}}
                />
                {fipixAccountDetailData?.is_active ? "Active" : "Inactive"}
              </p>
            }
          />
          <DescriptionItem
            icon={MailOpenIcon}
            label="Username (Email)"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixAccountDetailData?.username}
              </p>
            }
          />
          <DescriptionItem
            icon={UserIcon}
            label="Full name"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixAccountDetailData?.full_name}
              </p>
            }
          />
          <DescriptionItem
            icon={SparklesIcon}
            label="Event"
            content={organizationEventIds.map((event) =>
              fipixAccountDetailData?.events.includes(event.value) ? (
                <p className="text-sm leading-6" key={event.value}>
                  {event.label}
                </p>
              ) : (
                <div key={event.value}></div>
              )
            )}
          />
        </div>
      </div>
      {isFetching && isPreviousData ? <FullScreenLoading /> : <></>}
      <HistoryLodModal
        resource={`fipix/accounts/${params?.id}/history`}
        open={openLogModal}
        onClose={() => setOpenLogModal(false)}
        key={"fipix-log"}
      />
    </div>
  );
};
